import { useEffect, useRef, useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import { TrashIcon } from "@fawkes/ui";

import { WhatsAppLogo } from "../WhatsAppTile";

/**
 * Het eigen WhatsApp-nummer van een vestiging, als kaart op het vestigingenscherm.
 *
 * <p>Koppelen gaat met de koppelknop van Meta (Embedded Signup): de beheerder logt in een venster van
 * Facebook in, kiest of maakt een zakelijk account en een nummer, en Meta geeft twee dingen terug. Via
 * een {@code message}-event het nummer en het account ({@code phone_number_id}, {@code waba_id}), en via
 * de callback van {@code FB.login} een eenmalige code. Pas als we alle drie hebben gaan ze samen naar de
 * api; die geeft de code door aan het platform, dat hem bij Meta inruilt voor een sleutel.
 *
 * <p>In welke volgorde die twee aankomen staat nergens vast, dus allebei schrijven ze in dezelfde
 * {@code sessie} en wie als laatste komt verstuurt.
 */

interface KoppelInstellingen {
  beschikbaar: boolean;
  appId: string | null;
  configId: string | null;
  apiVersie: string | null;
}

interface Stand {
  gekoppeld: boolean;
  phoneNumberId: string | null;
  weergave: string | null;
  naam: string | null;
  gekoppeldOp: string | null;
  gekoppeldDoor: string | null;
  instellingen: KoppelInstellingen;
}

interface FbLoginAntwoord {
  authResponse?: { code?: string } | null;
  status?: string;
}

export interface FacebookSdk {
  init(opties: { appId: string; version: string; xfbml?: boolean; cookie?: boolean; autoLogAppEvents?: boolean }): void;
  login(callback: (antwoord: FbLoginAntwoord) => void, opties: Record<string, unknown>): void;
}

declare global {
  interface Window {
    FB?: FacebookSdk;
    fbAsyncInit?: () => void;
  }
}

const SDK_URL = "https://connect.facebook.net/en_US/sdk.js";

/** Hoe lang we na de code nog op het nummer van Meta wachten voordat we het opgeven. */
const WACHT_OP_NUMMER_MS = 15_000;

let sdkBelofte: Promise<FacebookSdk> | null = null;

/**
 * De JavaScript-sdk van Facebook laden en klaarzetten voor deze app.
 *
 * <p>Pas bij de klik en niet bij het openen van het scherm: zo komt er geen script van Facebook op de
 * pagina van iemand die alleen een adres komt wijzigen.
 */
export function laadFacebookSdk(appId: string, versie: string): Promise<FacebookSdk> {
  const zetKlaar = (fb: FacebookSdk) => {
    fb.init({ appId, version: versie, xfbml: false, autoLogAppEvents: true });
    return fb;
  };
  if (window.FB) {
    return Promise.resolve(zetKlaar(window.FB));
  }
  if (!sdkBelofte) {
    sdkBelofte = new Promise<FacebookSdk>((resolve, reject) => {
      window.fbAsyncInit = () => {
        if (window.FB) resolve(zetKlaar(window.FB));
        else reject(new Error("FB ontbreekt na het laden"));
      };
      const script = document.createElement("script");
      script.src = SDK_URL;
      script.async = true;
      script.defer = true;
      script.crossOrigin = "anonymous";
      script.onerror = () => {
        sdkBelofte = null;
        reject(new Error("sdk niet geladen"));
      };
      document.body.appendChild(script);
    });
  }
  return sdkBelofte;
}

/** Of een bericht echt van Facebook komt; elk ander venster kan ook een {@code message} sturen. */
function vanFacebook(origin: string): boolean {
  try {
    const host = new URL(origin).hostname;
    return host === "facebook.com" || host.endsWith(".facebook.com");
  } catch {
    return false;
  }
}

interface Sessie {
  code?: string;
  phoneNumberId?: string;
  wabaId?: string;
  verstuurd?: boolean;
}

export function WhatsAppKoppeling({ vestigingId }: { vestigingId: number }) {
  const t = useTranslate();
  const url = `vestigingen-beheer/${vestigingId}/whatsapp`;
  const { data, isLoading, isError, refetch } = useCustom<Stand>({ url, method: "get" });
  const { mutate } = useCustomMutation();
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const sessie = useRef<Sessie>({});
  const wachten = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stand = data?.data;

  const stopWachten = () => {
    if (wachten.current) clearTimeout(wachten.current);
    wachten.current = null;
  };

  const klaar = (melding: string | null) => {
    stopWachten();
    setBezig(false);
    setFout(melding);
  };

  // Een andere vestiging gekozen: een half afgemaakte koppeling hoort niet bij de nieuwe.
  useEffect(() => {
    sessie.current = {};
    stopWachten();
    setBezig(false);
    setFout(null);
  }, [vestigingId]);

  useEffect(() => stopWachten, []);

  /** Versturen zodra code én nummer er zijn, en maar één keer. */
  const probeerKoppelen = () => {
    const s = sessie.current;
    if (s.verstuurd || !s.code || !s.phoneNumberId || !s.wabaId) return;
    s.verstuurd = true;
    stopWachten();
    mutate(
      {
        url,
        method: "post",
        values: { code: s.code, wabaId: s.wabaId, phoneNumberId: s.phoneNumberId },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          klaar(null);
          refetch();
        },
        onError: (err) => klaar(err?.message || t("vestigingenBeheer.whatsapp.mislukt")),
      },
    );
  };
  // De listener hieronder blijft staan; via de ref roept hij altijd de nieuwste versie aan.
  const probeerRef = useRef(probeerKoppelen);
  probeerRef.current = probeerKoppelen;
  const klaarRef = useRef(klaar);
  klaarRef.current = klaar;

  useEffect(() => {
    const ontvang = (event: MessageEvent) => {
      if (!vanFacebook(event.origin)) return;
      let bericht: { type?: string; event?: string; data?: Record<string, unknown> };
      try {
        bericht = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch {
        return; // de sdk stuurt ook berichten die geen json zijn
      }
      if (!bericht || bericht.type !== "WA_EMBEDDED_SIGNUP") return;
      const gegevens = bericht.data ?? {};
      if (String(bericht.event ?? "").startsWith("FINISH")) {
        const phoneNumberId = gegevens.phone_number_id;
        const wabaId = gegevens.waba_id;
        if (typeof phoneNumberId === "string" && typeof wabaId === "string") {
          sessie.current.phoneNumberId = phoneNumberId;
          sessie.current.wabaId = wabaId;
          probeerRef.current();
        }
      } else if (bericht.event === "CANCEL") {
        sessie.current.verstuurd = true;
        klaarRef.current(t("vestigingenBeheer.whatsapp.afgebroken"));
      } else if (bericht.event === "ERROR") {
        sessie.current.verstuurd = true;
        const melding = typeof gegevens.error_message === "string" ? gegevens.error_message : null;
        klaarRef.current(melding
          ? `${t("vestigingenBeheer.whatsapp.metaFout")} ${melding}`
          : t("vestigingenBeheer.whatsapp.metaFout"));
      }
    };
    window.addEventListener("message", ontvang);
    return () => window.removeEventListener("message", ontvang);
  }, [t]);

  const start = async () => {
    const instellingen = stand?.instellingen;
    if (!instellingen?.beschikbaar || !instellingen.appId || !instellingen.configId) return;
    sessie.current = {};
    stopWachten();
    setFout(null);
    setBezig(true);
    let fb: FacebookSdk;
    try {
      fb = await laadFacebookSdk(instellingen.appId, instellingen.apiVersie || "v23.0");
    } catch {
      klaar(t("vestigingenBeheer.whatsapp.sdkFout"));
      return;
    }
    // Geen async callback: de sdk van Facebook weigert die.
    fb.login(
      (antwoord) => {
        const code = antwoord?.authResponse?.code;
        if (!code) {
          // Venster dichtgedaan, of geweigerd. Kwam er al een CANCEL of ERROR, dan staat die melding er al.
          if (!sessie.current.verstuurd) {
            sessie.current.verstuurd = true;
            klaar(t("vestigingenBeheer.whatsapp.afgebroken"));
          }
          return;
        }
        sessie.current.code = code;
        probeerRef.current();
        if (!sessie.current.verstuurd) {
          wachten.current = setTimeout(() => {
            if (!sessie.current.verstuurd) {
              sessie.current.verstuurd = true;
              klaar(t("vestigingenBeheer.whatsapp.geenNummer"));
            }
          }, WACHT_OP_NUMMER_MS);
        }
      },
      {
        config_id: instellingen.configId,
        response_type: "code",
        override_default_response_type: true,
        extras: { setup: {}, sessionInfoVersion: "3" },
      },
    );
  };

  const ontkoppel = () => {
    if (!window.confirm(t("vestigingenBeheer.whatsapp.ontkoppelenVraag"))) return;
    setFout(null);
    setBezig(true);
    mutate(
      { url, method: "delete", values: {}, successNotification: false, errorNotification: false },
      {
        onSuccess: () => {
          klaar(null);
          refetch();
        },
        onError: (err) => klaar(err?.message || t("vestigingenBeheer.whatsapp.mislukt")),
      },
    );
  };

  const beschikbaar = !!stand?.instellingen?.beschikbaar;

  return (
    <section className="vb-card">
      <div className="vb-card-head">
        <h3 className="vb-card-title">{t("vestigingenBeheer.group.whatsapp")}</h3>
        {stand?.gekoppeld && (
          <button type="button" className="vb-card-action" disabled={bezig} onClick={ontkoppel}>
            <TrashIcon width={14} height={14} />
            {t("vestigingenBeheer.whatsapp.ontkoppelen")}
          </button>
        )}
      </div>

      {isLoading ? (
        <p className="vb-card-note">{t("common.loading")}</p>
      ) : isError || !stand ? (
        <p className="vb-wa__fout">{t("vestigingenBeheer.whatsapp.laadFout")}</p>
      ) : (
        <div className="vb-wa">
          <span className={`vb-wa__beeld${stand.gekoppeld ? " vb-wa__beeld--aan" : ""}`} aria-hidden="true">
            <WhatsAppLogo size={22} />
          </span>
          <div className="vb-wa__zij">
            {stand.gekoppeld ? (
              <>
                <span className="vb-wa__label">{t("vestigingenBeheer.whatsapp.gekoppeld")}</span>
                <span className="vb-wa__nummer">{stand.weergave || stand.phoneNumberId}</span>
                {stand.naam && <span className="vb-wa__naam">{stand.naam}</span>}
                {stand.gekoppeldOp && (
                  <span className="vb-card-note">
                    {t("vestigingenBeheer.whatsapp.gekoppeldOp")}{" "}
                    {new Date(stand.gekoppeldOp).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" })}
                    {stand.gekoppeldDoor ? ` ${t("vestigingenBeheer.whatsapp.door")} ${stand.gekoppeldDoor}` : ""}
                  </span>
                )}
              </>
            ) : (
              <p className="vb-card-note">{t("vestigingenBeheer.whatsapp.nietGekoppeld")}</p>
            )}

            {beschikbaar ? (
              <>
                {!stand.gekoppeld && <p className="vb-card-note">{t("vestigingenBeheer.whatsapp.uitleg")}</p>}
                <button type="button" className="vb-logo__kies" disabled={bezig} onClick={() => void start()}>
                  {bezig
                    ? t("vestigingenBeheer.whatsapp.bezig")
                    : stand.gekoppeld
                      ? t("vestigingenBeheer.whatsapp.opnieuw")
                      : t("vestigingenBeheer.whatsapp.koppelen")}
                </button>
              </>
            ) : (
              <p className="vb-card-note">{t("vestigingenBeheer.whatsapp.nietIngericht")}</p>
            )}
            {fout && (
              <p className="vb-wa__fout" role="alert">
                {fout}
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
